/*
 * Description:
 * ---------------------
 * The approval page, phone first: what an AI agent asked for that needs a
 * person's yes (a key card at B3 or above, or a new approver list), as the
 * rows of what the approver is shown, in plain words from its text alone;
 * who asked and why; Approve with passkey or Decline. Beside every approval
 * it says how often this person said yes in the last hour and that a passkey
 * stops a forged approval, not a deceived one.
 *
 * Before it asks the passkey, the page works out the digest of the rows it
 * shows and the challenge over it and the statement the begin route names
 * (passkeys.shownOf, challengeOf), and goes on only when both are the
 * server's, so the passkey signs what the person read. Every box works both
 * out again and refuses any other. It never asks inside another page's
 * frame, where every box refuses the yes.
 *
 * With no approval named, it lists those waiting for this person (NeedsYou's
 * list). Fed by /api/v1/mesh/approvals (approvalApi.js, server/pkg/approvals),
 * and the investigation behind a key card by /api/v1/mesh/tasks; all wording
 * is in approvalWords.js.
 *
 * ! file components/ApprovalPage.js
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { NotServed, ROUTES, ask } from "./approvalApi";
import { challengeOf, credentialJSON, passkeyBlocker, requestOptions, sameBytes, shownOf } from "./passkeys";
import * as W from "./approvalWords";
import "./Firewall.css";
import "./Mesh.css";
import "./Approvals.css";

const WAITING_MS = 5000;
const DECIDED_MS = 30000;

const Status = ({ tone, children }) => (
  <span className={`mesh-answer is-${tone}`}><span className="mesh-dot" aria-hidden="true" /> {children}</span>
);
const Problem = ({ children }) => (
  <div className="fw-notice is-bad" role="alert"><FiAlertTriangle size={16} /> <span>{children}</span></div>
);
const unserved = <FeatureUnavailableNotice compact nodeId="this Weavian server" feature="Approvals" detail={W.NOT_ON_SERVER} />;

function Rows({ a, now }) {
  return (
    <dl className="ap-rows">
      {W.renderingRows(a, now).map((r, i) => (
        <div key={i} className={`ap-row${r.repeat ? " is-more" : ""}`}>
          <dt>{r.repeat ? "" : r.label}</dt>
          <dd>
            {r.block ? <pre className="ap-text">{r.plain}</pre> : <span className="ap-plain">{r.plain}</span>}
            {r.raw && <code className="ap-raw">{r.raw}</code>}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function Approval({ serverApiUrl, id, onAll, onDecided }) {
  const [view, setView] = useState(null);
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const [notServed, setNotServed] = useState(false);
  // Whether the rows shown are the ones the server fingerprinted: null until
  // this page has worked it out.
  const [rowsOk, setRowsOk] = useState(null);
  const [busy, setBusy] = useState("");
  const [problem, setProblem] = useState("");
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const read = useCallback(async () => {
    try {
      const out = await ask(serverApiUrl, ROUTES.approval(id));
      if (!mounted.current) return;
      setView(out);
      setError("");
    } catch (e) {
      if (!mounted.current) return;
      if (e instanceof NotServed) setNotServed(true);
      else setError(e.message);
    }
  }, [serverApiUrl, id]);
  const a = view?.approval;
  useVisibleInterval(read, !a || a.status === "waiting" ? WAITING_MS : DECIDED_MS);

  /* The rows shown are checked against the server's digest of them as soon
     as they arrive, so a mismatch is said before anyone reaches for a
     passkey. */
  const { rendering, shown, task: taskId } = a || {};
  useEffect(() => {
    if (rendering === undefined || passkeyBlocker()) return undefined;
    let live = true;
    shownOf(rendering).then((s) => live && setRowsOk(s === shown));
    return () => { live = false; };
  }, [rendering, shown]);

  /* The investigation a key card was asked for, in the console's words. A
     task the server no longer lists leaves the sentence without it. */
  useEffect(() => {
    if (!taskId) return undefined;
    let live = true;
    ask(serverApiUrl, ROUTES.task(taskId)).then((out) => live && setTask(out.task || null)).catch(() => live && setTask(null));
    return () => { live = false; };
  }, [serverApiUrl, taskId]);

  const settle = (out) => {
    if (!mounted.current) return;
    setView((v) => ({ ...v, approval: out.approval, fatigue: out.fatigue ? { ...v.fatigue, [v.you]: out.fatigue } : v.fatigue }));
    onDecided();
  };

  const approve = async () => {
    setBusy("approve");
    setProblem("");
    try {
      const blocked = passkeyBlocker();
      if (blocked) throw new Error(W.BLOCKED[blocked]);
      const seen = await shownOf(a.rendering);
      if (seen !== a.shown) throw new Error(W.SHOWN_MISMATCH);
      // A rules bundle names its signer, so each approver signs their own
      // draft of it: the begin route says which statement the challenge is over.
      const begun = await ask(serverApiUrl, ROUTES.approveBegin(a.id), {});
      const options = requestOptions(begun);
      if (!sameBytes(options.challenge, await challengeOf(a.kind, begun.digest || a.digest, seen))) throw new Error(W.MISMATCH);
      const cred = await navigator.credentials.get({ publicKey: options }).catch((e) => { throw new Error(W.passkeyError(e, false)); });
      settle(await ask(serverApiUrl, ROUTES.approveFinish(a.id), credentialJSON(cred)));
    } catch (e) {
      if (mounted.current) setProblem(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
    } finally {
      if (mounted.current) setBusy("");
    }
  };

  const decline = async () => {
    setBusy("decline");
    setProblem("");
    try {
      settle(await ask(serverApiUrl, ROUTES.decline(a.id), {}));
    } catch (e) {
      if (mounted.current) setProblem(e instanceof NotServed ? W.NOT_ON_SERVER : e.message);
    } finally {
      if (mounted.current) setBusy("");
    }
  };

  if (notServed) return unserved;
  if (!a) return <div className="fw ap">{error ? <Problem>{error}</Problem> : <p className="fw-muted">Reading…</p>}</div>;

  const now = new Date();
  const st = W.stateOf(a, now);
  const said = W.outcome(a, now);
  const yours = W.yourPart(view, now);
  const fatigue = W.fatigueNote(view.fatigue?.[view.you]);
  const blocked = passkeyBlocker();
  const may = a.status === "waiting" && view.approver && !W.saidYes(a, view.you);
  return (
    <div className="fw ap">
      {error && <Problem>{error}</Problem>}
      <button type="button" className="fw-link ap-back" onClick={onAll}>‹ {W.BACK_TO_ALL}</button>
      <section className="ap-ask" aria-live="polite">
        <Status tone={st.tone}>{st.text}</Status>
        <h2 className="ap-headline">{W.headline(a)}</h2>
        {said && <p className="ap-said">{said}</p>}
        {yours && <p className="ap-said">{yours}</p>}
      </section>

      <section className="fw-card ap-card">
        <h3 className="fw-card-title">{W.WHAT_YOU_APPROVE}</h3>
        <Rows a={a} now={now} />
        <details className="mesh-how">
          <summary>{W.SIGNS}</summary>
          <p className="fw-muted">{W.signsLead(a)}</p>
          <dl className="ap-prints">
            <dt>{W.CARD_PRINT}</dt>
            <dd><code>{a.digest}</code></dd>
            <dt>{W.ROWS_PRINT}</dt>
            <dd><code>{a.shown}</code>{rowsOk && <span className="fw-muted">{W.ROWS_CHECKED}</span>}</dd>
          </dl>
          <p className="ap-print-label">{W.ROWS_AS_SIGNED}</p>
          <pre className="ap-text">{a.rendering}</pre>
        </details>
      </section>

      <section className="fw-card ap-card">
        <h3 className="fw-card-title">{W.WHO_ASKS}</h3>
        <p className="ap-said">{W.askedBy(a, task, now)}</p>
        <p className="fw-muted">{W.ASKED_NOT_SIGNED}</p>
      </section>

      <section className="fw-card ap-card ap-decide">
        <p className={`ap-fatigue${fatigue.warn ? " is-warn" : ""}`}>{fatigue.text}</p>
        <p className="ap-deceived"><strong>{W.DECEIVED}</strong> {W.DECEIVED_WHY}</p>
        {may && blocked && <Problem>{W.BLOCKED[blocked]}</Problem>}
        {may && rowsOk === false && <Problem>{W.SHOWN_MISMATCH}</Problem>}
        {problem && <Problem>{problem}</Problem>}
        {may && (
          <div className="ap-buttons">
            <button type="button" className="fw-btn is-primary is-big" disabled={!!busy || !!blocked || rowsOk === false} onClick={approve}>
              {busy === "approve" ? W.APPROVING : W.APPROVE}
            </button>
            <button type="button" className="fw-btn is-big" disabled={!!busy} onClick={decline}>{W.DECLINE}</button>
          </div>
        )}
      </section>
    </div>
  );
}

function Waiting({ waiting, onOpen }) {
  const { approvals, error } = waiting;
  const now = new Date();
  if (error === "unserved") return unserved;
  if (!approvals) return <div className="fw ap">{error ? <Problem>{error}</Problem> : <p className="fw-muted">Reading…</p>}</div>;
  return (
    <div className="fw ap">
      {error && <Problem>{error}</Problem>}
      {approvals.length === 0 && <section className="fw-card"><p className="ap-said">{W.NOTHING_WAITS}</p></section>}
      {approvals.map((a) => {
        const st = W.stateOf(a, now);
        return (
          <section key={a.id} className="fw-card ap-card">
            <Status tone={st.tone}>{st.text}</Status>
            <h3 className="ap-headline is-small">{W.headline(a)}</h3>
            <div className="ap-buttons">
              <button type="button" className="fw-btn is-primary" onClick={() => onOpen(a.id)}>{W.LOOK}</button>
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default function ApprovalPage({ serverApiUrl, id, waiting, onOpen }) {
  return id
    ? <Approval key={id} serverApiUrl={serverApiUrl} id={id} onAll={() => onOpen("")} onDecided={waiting.refresh} />
    : <Waiting waiting={waiting} onOpen={onOpen} />;
}
